import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function DeleteAccountSection() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const handleDelete = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account", { body: { confirm: "EXCLUIR" } });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      queryClient.clear();
      await supabase.auth.signOut();
      toast({ title: "Conta excluída", description: "Seus dados foram removidos da plataforma." });
      navigate("/");
    } catch (e: any) {
      toast({ variant: "destructive", title: "Erro ao excluir conta", description: e.message || "Tente novamente." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-destructive/50">
      <CardHeader>
        <CardTitle className="text-destructive">Zona de perigo</CardTitle>
        <CardDescription>Excluir sua conta remove permanentemente seu perfil e seus dados. Essa ação não pode ser desfeita.</CardDescription>
      </CardHeader>
      <CardContent>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); setText(""); }}>
          <DialogTrigger asChild>
            <Button variant="destructive"><Trash2 className="mr-2 h-4 w-4" />Excluir minha conta</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Excluir conta definitivamente?</DialogTitle>
              <DialogDescription>
                Perfil, currículo, candidaturas, progresso e assinaturas serão removidos/cancelados. Seus posts na comunidade ficarão como "Usuário removido". Digite <strong>EXCLUIR</strong> para confirmar.
              </DialogDescription>
            </DialogHeader>
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="EXCLUIR" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button variant="destructive" disabled={text !== "EXCLUIR" || loading} onClick={handleDelete}>
                {loading ? "Excluindo..." : "Excluir conta"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
